import { IconSearch } from '@tabler/icons-react';
import {
    SearchDropdown,
    SearchDropdownHandle,
} from 'components/SearchBar/SearchDropdown';
import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'shared/Input';
import useDebounce from 'utils/hooks/useDebounce';
import { useKeyboardShortcut } from 'utils/hooks/useKeyboardShortcut';

const SEARCH_DEBOUNCE = 250;

export const SearchBar = () => {
    const { t } = useTranslation('layout', { keyPrefix: 'search-bar' });
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<SearchDropdownHandle>(null);

    const [searchValue, setSearchValue] = useState('');
    const searchDebounce = useDebounce(searchValue, SEARCH_DEBOUNCE);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useKeyboardShortcut({ key: 'k', ctrlKey: true }, () => {
        inputRef.current?.focus();
    });

    const handleKeydown = useCallback((event: KeyboardEvent) => {
        const { key } = event;
        if (key === 'ArrowDown') {
            dropdownRef.current?.next();
            event.preventDefault();
        } else if (key === 'ArrowUp') {
            dropdownRef.current?.previous();
            event.preventDefault();
        } else if (key === 'Enter') {
            dropdownRef.current?.enter();
            event.preventDefault();
        } else if (key === 'Escape') {
            inputRef.current?.blur();
        }
    }, []);

    const onSelected = useCallback(() => {
        setSearchValue('');
        inputRef.current?.blur();
    }, []);

    return (
        <div
            className="relative lg:w-96 w-full sm:text-xs"
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setDropdownOpen(false)}
        >
            {/* workaround to remove unwanted spacing generated by empty label */}
            <div className="[&>label]:gap-0">
                <TextInput
                    ref={inputRef}
                    role="combobox"
                    aria-controls="search-dropdown"
                    color="light"
                    sizing="md"
                    icon={({ ref, className = '', ...props }) => (
                        <IconSearch
                            {...props}
                            className={`${className} !text-gray-300`}
                        />
                    )}
                    placeholder={t('placeholder')}
                    value={searchValue}
                    onChange={(search) => {
                        setSearchValue(search);
                    }}
                    onBlur={(event) => {
                        const relatedTarget = event.relatedTarget;
                        if (dropdownRef.current?.contains(relatedTarget)) {
                            event.stopPropagation();
                        }
                    }}
                    onKeyDown={handleKeydown}
                />
            </div>

            <SearchDropdown
                ref={dropdownRef}
                id="search-dropdown"
                className="absolute w-full"
                role="listbox"
                onFocus={() => {
                    // focus on input element instead
                    inputRef.current?.focus();
                }}
                open={dropdownOpen}
                searchQuery={searchDebounce}
                onSelected={onSelected}
            />
        </div>
    );
};
